import React, { useMemo } from 'react'
import { ListItem, ListItemText, ListItemAvatar } from '../../components/list';
import Avatar from '../../components/avatar';
import Typography, { TypographyColor } from '../../components/typography';
import Box from '../../components/box';
import { BookingStatus } from '../../apilibrary/types/booking';
import Button from '../../components/button';
import { Divider } from '@mui/material';
import Link from '../../components/router/link';
import { Location } from '../../models';

export interface BookingListItemProps {
  studioId: string
  studioName: string
  studioImageUrl: string
  location?: Location
  isProvider: boolean
  providedBy: string
  serviceStartTime: Date
  serviceEndTime: Date
  status: BookingStatus
  handleRequestBookingResponse?: (accept: boolean) => Promise<void>
  handlePayBooking?: () => Promise<void>
}

const BookingListItem: React.FC<BookingListItemProps> = ({
  studioId,
  studioName,
  studioImageUrl,
  providedBy,
  isProvider,
  location,
  serviceStartTime,
  serviceEndTime,
  handleRequestBookingResponse,
  handlePayBooking,
  status
}) => {
  const [loading, setLoading] = React.useState(false)

  const bookingStatusColor = useMemo(() => {
    switch (status) {
      case BookingStatus.PENDING_PAYMENT:
      case BookingStatus.REQUESTED:
      case BookingStatus.PAYMENT_PROCESSING:
        return TypographyColor.INFO
      case BookingStatus.CONFIRMED:
        return TypographyColor.SUCCESS
      case BookingStatus.CANCELLED:
      case BookingStatus.PAYMENT_FAILED:
      case BookingStatus.DECLINED:
        return TypographyColor.ERROR
      default:
        return TypographyColor.SECONDARY
    }
  }, [status])

  const primary = useMemo(() => {
    const handleClickRespondToBookingRequest = async (accept: boolean): Promise<void> => {
      try {
        if (!handleRequestBookingResponse) {
          return
        }
        setLoading(true)
        await handleRequestBookingResponse(accept)
        setLoading(false)
      } catch (error) {
        console.log(error)
        throw error
      } finally {
        setLoading(false)
      }
    }

    const handleClickPayForBooking = async (): Promise<void> => {
      try {
        if (!handlePayBooking) {
          return
        }
        setLoading(true)
        await handlePayBooking()
      } catch (error) {
        console.log(error)
        throw error
      }
    }

    return (
      <>
        <Box display={'flex'} justifyContent={'space-between'} alignItems={'flex-start'}>
          <Link to={`/studio/${studioId}`}>
            <Typography variant='h4' fontSize={'20px'}>
              {studioName}
            </Typography>
          </Link>
          {(status === BookingStatus.REQUESTED && isProvider) && (
            <Box display={'flex'} gap={2}>
              <Button disabled={loading} size='small' variant='outlined' color='success' onClick={async () => { await handleClickRespondToBookingRequest(true); }}>
                Accept
              </Button>
              <Button disabled={loading} size='small' variant='outlined' color='warning' onClick={async () => { await handleClickRespondToBookingRequest(false); }}>
                Decline
              </Button>
            </Box>
          )}
          {((status === BookingStatus.PENDING_PAYMENT || status === BookingStatus.PAYMENT_FAILED) && !isProvider) && (
            <Button disabled={loading} size='small' variant='outlined' color='info' onClick={handleClickPayForBooking}>
              Pay
            </Button>
          )}
        </Box>
        <Divider sx={{ mt: 1 }} />
      </>
    )
  }, [studioId, studioName, status, isProvider, loading, handleRequestBookingResponse, handlePayBooking])

  const statusText = useMemo(() => {
    switch (status) {
      case BookingStatus.PENDING_PAYMENT:
        return 'Pending Payment'
      case BookingStatus.PAYMENT_PROCESSING:
        return 'Payment Processing'
      case BookingStatus.PAYMENT_FAILED:
        return 'Payment Failed -- Please try again'
      case BookingStatus.REQUESTED:
        return 'Requested'
      case BookingStatus.CONFIRMED:
        return 'Confirmed'
      case BookingStatus.CANCELLED:
        return 'Cancelled'
      case BookingStatus.DECLINED:
        return 'Declined'
    }
  }, [status])

  return (
    <ListItem sx={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 2,
      px: 0
    }}>
      <ListItemAvatar>
        <Avatar sx={{
          height: '165px',
          width: '155px',
          // aspectRatio: '4/3',
          borderRadius: '10%'
        }} src={studioImageUrl} />
      </ListItemAvatar>
      <ListItemText
        primary={primary}
        secondary={
          <Box mt={1} display={'flex'} flexDirection={'column'} gap={1}>
            <Typography variant='caption' fontSize={'14px'} display={'block'} >
              {serviceStartTime.toLocaleDateString()} {serviceStartTime.toLocaleTimeString()} - {serviceEndTime.toLocaleTimeString()}
            </Typography>
            <Box display={'flex'} gap={1}>
              <Typography variant='caption' fontSize={'14px'} fontWeight={400}>
                Provided by
              </Typography>
              <Typography variant='caption' fontSize={'14px'} fontWeight={500}>
                {providedBy}
              </Typography>
            </Box>
            {status === BookingStatus.CONFIRMED && location && (
              <Box display={'flex'} gap={1}>
                <a
                  style={{
                    textDecoration: 'none'
                  }}
                  href={`https://www.google.com/maps/search/?api=1&query=${location.streetAddress} ${location.city}, ${location.state} ${location.zipCode}`}
                  target='_blank'
                  rel='noreferrer'
                >
                  <Typography color={TypographyColor.SECONDARY} variant='subtitle2' fontWeight={400}>
                    {location.streetAddress} {location.aptNumber && `#${location.aptNumber}`}
                    {location.city}, {location.state} {location.zipCode}
                  </Typography>
                </a>
              </Box>
            )}
            <Typography variant='caption' color={bookingStatusColor} fontSize={'12x'} display={'block'}>
              {statusText}
            </Typography>
          </Box>
        }
      />
    </ListItem>
  )
}

export default BookingListItem
